import { useEffect, useState } from 'react';
import { OVERLAY_PANEL_GAP } from '../../../pages/Shoreline/theme';
import { useDispatch, useSelector } from 'react-redux';
import { useErrorNotice } from '../../../../hooks/useErrorNotice';
import * as turf from '@turf/turf';
import {
  Box,
  Grow,
  Card,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  LinearProgress,
} from '@material-ui/core';
import { GestureOutlined, LinearScale, GetApp } from '@material-ui/icons';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { lineToPoints, circleToPolyline } from '../../../../algorithms/utils/turf';
import {
  MIN_TRANSECT_STEP,
  MAX_TRANSECT_STEP,
  MIN_TRANSECT_EXTENT,
  MAX_TRANSECT_EXTENT,
} from '../../../../common/utils';
import { TerraDrawManager } from '../../../../common/TerraDrawManager';
import ShapeEditControl from '../../map/ShapeEditControl';
import { BASELINE_VERTEX_EDITING_ENABLED, useShapeVertexEditing } from '../../../../hooks/useShapeVertexEditing';
import * as Metadata from '../../../../common/metadata';
import { Actions as Map } from '../../../../store/ducks/map';
import { Actions as ShoreSAnalysis } from '../../../../store/ducks/shoreSAnalysis';
import { MERGE_TOLERANCE_DAYS, MERGE_DIRECTION } from '../../../../algorithms/shoreS';
import { readFileAndGetCoordinates } from '../shoreR/utils';
import SimpleInfoDialog from '../../dialog/SimpleInfoDialog';
import ShoreSErrorPlots from './ShoreSErrorPlots';
import ShoreSMetricsPanel from './ShoreSMetricsPanel';
import ShoreSExportDialog from '../../dialog/ShoreSExportDialog';
import { readSelectedImages } from './utils';

const REFERENCE_COASTLINE_GROUP = 'shoreSCoastlineData';

const TRANSECT_CONFIG = {
  CIRCLE_RADIUS: 1,
  POLYLINE_STYLE: {
    strokeColor: 'purple',
    strokeOpacity: 1.0,
    strokeWeight: 3,
    geodesic: true,
    clickable: true,
    zIndex: 90,
  },
};

/**
 * @function ShoreSAnalysisSteps
 * @description Mirrors Shore-R's real analysis flow (`ShoreRAnalysisSteps.jsx`)
 * as closely as possible: a persistent menu card ("Desenhar Linha Base" /
 * "Configurar Transectos", no "Selecionar Imagens" button, since Shore-S's
 * images are already confirmed earlier in the acquisition wizard), the same
 * TerraDrawManager + AOI-check + confirm-dialog baseline flow with KML
 * import, and the same single "configure transects" overlay (step/extent +
 * KML import), extended with the merge tolerance/direction fields Shore-R has
 * no equivalent for. The reference-coastline extraction is no longer part of
 * this step: it runs on its own as soon as the page opens, so the "Confirmar"
 * button only kicks off the transect-driven half and stays disabled until
 * `extraction.status === 'READY'`.
 * @param {Object} props - Component props.
 * @param {Function} props.t - Translation function.
 * @param {Object} props.mapRef - Ref to the Google Maps instance.
 * @param {Object} props.roiCoords - Ref to the ROI polygon.
 * @param {Object} props.baseLine - Current baseline state.
 * @param {Function} props.setBaseLine - Baseline state setter.
 * @param {Object} props.drawingManager - Ref to the TerraDrawManager.
 * @param {Object} props.extraction - The `shoreSAnalysis.extraction` sub-state.
 * @see {@link handleDrawBaseline} - starts drawing the baseline.
 * @see {@link generateTransects} - creates transects along the baseline.
 * @see {@link handleRun} - runs the validation pipeline.
 * **Called by:
 * @see {ProcessingShoreSPage}
 * @memberof module:ShoreS
 * @returns {JSX.Element} The Shore-S analysis workflow UI.
 */
const ShoreSAnalysisSteps = ({ t, mapRef, roiCoords, baseLine, setBaseLine, drawingManager, extraction }) => {
  const [drawingBaseline, setDrawingBaseline] = useState(false);
  const [confirmDrawing, setConfirmDrawing] = useState(false);
  const [baseLineCoords, setBaseLineCoords] = useState([]);
  const [settingsTransectsEnabled, setSettingsTransectsEnabled] = useState(false);
  const [isSettings, setIsSettings] = useState(false);
  const [transectsStep, setTransectsStep] = useState(100);
  const [transectsExtent, setTransectsExtent] = useState(1600);
  const [transectsObjects, setTransectsObjects] = useState([]);
  const [confirmTransectsConfigEnabled, setConfirmTransectsConfigEnabled] = useState(true);
  const [toleranceInDays, setToleranceInDays] = useState(MERGE_TOLERANCE_DAYS);
  const [direction, setDirection] = useState(MERGE_DIRECTION);
  const [analysisDisabled, setAnalysisDisabled] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showImageChooserHelp, setShowImageChooserHelp] = useState(false);
  const [showMergeHelp, setShowMergeHelp] = useState(false);
  const [showPlots, setShowPlots] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const dispatch = useDispatch();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const loading = useSelector((s) => s.shoreSAnalysis.loading);
  const error = useSelector((s) => s.shoreSAnalysis.error);
  const errorMetrics = useSelector((s) => s.shoreSAnalysis.errorMetrics);

  useEffect(() => {
    if (errorMetrics) setAnalysisDisabled(true);
  }, [errorMetrics]);

  const extractionReady = extraction?.status === 'READY';

  /**
   * @function isPolygonInsideAOI
   * @description Checks whether every vertex of `polygonInner` (the drawn baseline) falls inside
   * `polygonOuter` (the ROI).
   * @param {Object} polygonInner - google.maps.Polyline/Polygon overlay.
   * @param {Object} polygonOuter - google.maps.Polygon (ROI).
   * **Called by:
   * @see {handleDrawing}
   * @memberof module:ShoreS
   * @returns {boolean}
   */
  function isPolygonInsideAOI(polygonInner, polygonOuter) {
    for (let i = 0; i < polygonInner.getPath().getLength(); i++) {
      if (!window.google.maps.geometry?.poly.containsLocation(polygonInner.getPath().getAt(i), polygonOuter)) {
        return false;
      }
    }
    return true;
  }

  /**
   * @function handleDrawing
   * @description Handles the TerraDrawManager's `overlaycomplete` event for the baseline: if the
   * drawn polyline is fully inside the ROI, stores its coordinates and opens the confirm dialog;
   * otherwise alerts the user and discards the drawing.
   * @param {Object} polyg - `{ overlay }`, the completed drawing overlay.
   * **Called by:
   * @see {handleDrawBaseline} - via the TerraDrawManager's `overlaycomplete` listener.
   * @see {readBaselineFileData} - after importing a baseline from a KML.
   * @memberof module:ShoreS
   * @returns {void}
   */
  function handleDrawing(polyg) {
    if (isPolygonInsideAOI(polyg.overlay, roiCoords.current)) {
      setConfirmDrawing(true);
      setBaseLine(polyg);
      drawingManager.current.setDrawingMode(null);
      dispatch(Map.completeDrawing());
      const coords = polyg.overlay
        .getPath()
        .getArray()
        .map((coord) => [coord.lng(), coord.lat()]);
      setBaseLineCoords(coords);
      baselineEditing.setShape(polyg.overlay, 'polyline');
      return;
    }
    notifyError(t('forms.shorelineAnalysis.baselineAOIDrawAlert'));
    polyg.overlay.setMap(null);
  }

  /**
   * @function handleDrawBaseline
   * @description Starts (or restarts) baseline drawing: creates the TerraDrawManager on first
   * use, clears any previously drawn baseline, and enters polyline drawing mode.
   * **Called by:
   * @see {ShoreSAnalysisSteps} - via the "Desenhar Linha Base" button's onClick.
   * @see {handleRedo}
   * @memberof module:ShoreS
   * @returns {void}
   */
  const handleDrawBaseline = () => {
    if (!drawingManager.current) {
      drawingManager.current = new TerraDrawManager({
        drawingMode: 'polyline',
        drawingControl: false,
        polylineOptions: { strokeColor: '#03d4f0ff', strokeOpacity: 1, strokeWeight: 3 },
      });
      drawingManager.current.setMap(mapRef.current);
      drawingManager.current.addListener('overlaycomplete', handleDrawing);
    } else {
      drawingManager.current.setDrawingMode('polyline');
    }
    if (baseLine) baseLine.overlay.setMap(null);
    setBaseLine(null);
  };

  /**
   * @function handleRedo
   * @description Discards the current TerraDrawManager/baseline entirely and starts a fresh
   * drawing, used by the confirm dialog's "Refazer" button.
   * **Called by:
   * @see {ShoreSAnalysisSteps} - via the confirm dialog's "Refazer" button's onClick.
   * @memberof module:ShoreS
   * @returns {void}
   */

  /**
   * @description Lets the user drag the drawn baseline's existing points, re-checking after each
   * adjustment that it still lies inside the AOI — the same rule `handleDrawing` enforces when the
   * baseline is first drawn.
   * @see {@link useShapeVertexEditing}
   * @see {@link areBaselineCoordinatesInsideAOI}
   * @memberof module:ShoreS
   */
  const baselineEditing = useShapeVertexEditing({
    onChange: setBaseLineCoords,
    validate: (coords) => (areBaselineCoordinatesInsideAOI(coords) ? '' : t('forms.map.editOutsideAOI')),
    enabled: BASELINE_VERTEX_EDITING_ENABLED,
  });

  /**
   * @function areBaselineCoordinatesInsideAOI
   * @description Checks whether every `[lng, lat]` pair falls inside the region of interest. The
   * coordinate-based counterpart of `isPolygonInsideAOI`, used while editing, when the new vertices
   * arrive as plain coordinates rather than as an overlay.
   * @param {Array<Array<Number>>} coords - Baseline coordinates as [lng, lat] pairs.
   * @see {@link isPolygonInsideAOI}
   * @memberof module:ShoreS
   * @returns {Boolean} True when the whole baseline is inside the AOI.
   */
  function areBaselineCoordinatesInsideAOI(coords) {
    if (!roiCoords.current) return true;

    return coords.every(([lng, lat]) =>
      window.google.maps.geometry?.poly.containsLocation(new window.google.maps.LatLng(lat, lng), roiCoords.current),
    );
  }

  const handleRedo = () => {
    if (drawingManager.current) {
      drawingManager.current.cleanup();
      drawingManager.current.setMap(null);
      drawingManager.current = null;
    }
    baseLine && baseLine.overlay.setMap(null);
    baselineEditing.setShape(null);
    setBaseLine(null);
    setConfirmDrawing(false);
    setDrawingBaseline(true);
    handleDrawBaseline();
  };

  /**
   * @function readBaselineFileData
   * @description Reads an imported KML file's coordinates and draws them as the baseline
   * polyline, feeding the result through `handleDrawing` exactly as a hand-drawn baseline would
   * (same AOI check and confirm-dialog flow).
   * @param {Event} e - The file input's change event.
   * **Called by:
   * @see {ShoreSAnalysisSteps} - via the "Importar Linha Base" file input's onChange.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const readBaselineFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = () => {
        const baselineFile = readFileAndGetCoordinates(reader.result, t, notifyError);
        if (!baselineFile || baselineFile.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        const formattedCoordinates = baselineFile[0].map((coord) => ({ lat: coord[1], lng: coord[0] }));
        const polyline = new window.google.maps.Polyline({
          path: formattedCoordinates,
          strokeColor: '#03d4f0ff',
          strokeOpacity: 1,
          strokeWeight: 3,
          map: mapRef.current,
        });
        handleDrawing({ overlay: polyline });
      };
      if (file) reader.readAsText(file);
    } catch (e) {
      console.error('Error reading baseline file:', e);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  /**
   * @function importStepExtentTransects
   * @description Derives `transectsStep`/`transectsExtent` from an imported set of transect
   * lines: the majority (most common, rounded) transect length becomes the extent, and the
   * majority distance between consecutive transect centers becomes the step.
   * @param {Array<Array<Array<number>>>} allTransects - Imported transect endpoints, each transect a `[[x1,y1], [x2,y2]]` pair of points.
   * **Called by:
   * @see {readTransectsFileData}
   * @memberof module:ShoreS
   * @returns {void}
   */
  const importStepExtentTransects = (allTransects) => {
    if (!allTransects || allTransects.length === 0) {
      notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
      return;
    }
    const getMajority = (array) => {
      const count = array.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
      return Math.round(Number(Object.keys(count).reduce((a, b) => (count[a] > count[b] ? a : b))));
    };

    const extensions = allTransects.map((transect) =>
      turf.distance(turf.point(transect[0]), turf.point(transect[1]), { units: 'meters' }),
    );
    const extension = Math.round(getMajority(extensions));

    const centerTransects = allTransects.map((transect) => {
      const lng = (transect[0][0] + transect[1][0]) / 2;
      const lat = (transect[0][1] + transect[1][1]) / 2;
      return [lng, lat];
    });

    if (centerTransects.length < 2) {
      notifyError(t('forms.shorelineAnalysis.transectsSettings.alert.importTransectQuantity'));
      return;
    }
    const steps = [];
    for (let i = 0; i < centerTransects.length - 1; i++) {
      steps.push(turf.distance(centerTransects[i], centerTransects[i + 1], { units: 'meters' }));
    }
    const step = Math.round(getMajority(steps));

    setTransectsExtent(extension);
    setTransectsStep(step);
  };

  /**
   * @function readTransectsFileData
   * @description Reads an imported KML file's transect lines and derives the step/extent
   * settings from them (see `importStepExtentTransects`).
   * @param {Event} e - The file input's change event.
   * **Called by:
   * @see {ShoreSAnalysisSteps} - via the "Importar Transectos" file input's onChange.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const readTransectsFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = async () => {
        const allTransects = await readFileAndGetCoordinates(reader.result, t, notifyError);
        if (!allTransects || allTransects.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        importStepExtentTransects(allTransects);
      };
      if (file) reader.readAsText(file);
    } catch (e) {
      console.error('Error reading transects file:', e);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  /**
   * @function generateTransects
   * @description Client-side preview of the transects: walks points spaced `transectsStep` apart
   * along the baseline, and for each one builds a full transect line perpendicular to the
   * baseline, scaled to `transectsExtent`. Purely a visual preview — the actual analysis
   * regenerates transects server-side from the same step/extent (see `shoreSAnalysis/saga`).
   * **Called by:
   * @see {ShoreSAnalysisSteps} - via the transects-config effect, whenever step/extent change.
   * @memberof module:ShoreS
   * @returns {Array<{transect: number, sides: Array<{side: number, coords: Array}>}>}
   */
  const generateTransects = () => {
    const points = lineToPoints(baseLine.overlay, transectsStep);
    const outputTransects = [];
    points.forEach((point, i) => {
      if (i === 0) return;
      const circle = circleToPolyline(
        new window.google.maps.Circle({ center: point, radius: TRANSECT_CONFIG.CIRCLE_RADIUS }),
      );
      const circleLineCoords = circle.map((coord) => {
        const { lat, lng } = coord.toJSON();
        return [lng, lat];
      });
      const turfCircle = turf.lineString(circleLineCoords);
      const turfBaseLine = turf.lineString(baseLineCoords);
      const turfIntersection = turf.lineIntersect(turfCircle, turfBaseLine);
      const turfTransect = turf.lineString([
        [turfIntersection.features[0].geometry?.coordinates[0], turfIntersection.features[0].geometry?.coordinates[1]],
        [point.toJSON().lng, point.toJSON().lat],
      ]);
      const pivot = [point.toJSON().lng, point.toJSON().lat];
      const turfTransectRotate = turf.transformRotate(turf.clone(turfTransect), 90, { pivot });
      const turfTransectRotate2 = turf.transformRotate(turf.clone(turfTransect), -90, { pivot });
      const turfFullTransect = turf.multiLineString([
        turfTransectRotate.geometry?.coordinates,
        turfTransectRotate2.geometry?.coordinates,
      ]);
      const scaleTransect = turf.transformScale(turfFullTransect, transectsExtent / 2);
      outputTransects.push({
        transect: i,
        sides: [
          { side: 1, coords: scaleTransect.geometry?.coordinates[0] },
          { side: 2, coords: scaleTransect.geometry?.coordinates[1] },
        ],
      });
    });
    return outputTransects;
  };

  useEffect(() => {
    const isInvalid =
      transectsExtent < MIN_TRANSECT_EXTENT ||
      transectsExtent > MAX_TRANSECT_EXTENT ||
      transectsStep < MIN_TRANSECT_STEP ||
      transectsStep > MAX_TRANSECT_STEP;
    setConfirmTransectsConfigEnabled(!isInvalid);
    if (!isSettings || baseLineCoords.length < 2) return;
    transectsObjects.forEach((object) => object.setMap(null));
    setTransectsObjects([]);
    if (isInvalid) return;
    const newTransects = generateTransects();
    const tempTransectsObjects = [];
    newTransects.forEach((object) => {
      const sidesMaps0 = object.sides[0].coords.map((arr) => ({ lat: arr[1], lng: arr[0] }));
      const sidesMaps1 = object.sides[1].coords.map((arr) => ({ lat: arr[1], lng: arr[0] }));
      const complete = [sidesMaps0.flat(), sidesMaps1.flat()].flat();
      const polyline = new window.google.maps.Polyline({
        path: complete,
        transect: object.transect,
        ...TRANSECT_CONFIG.POLYLINE_STYLE,
      });
      polyline.setMap(mapRef.current);
      tempTransectsObjects.push(polyline);
    });
    setTransectsObjects(tempTransectsObjects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transectsStep, transectsExtent, isSettings]);

  /**
   * @function handleRun
   * @description Confirms the transects settings and dispatches the full validation run
   * (`ShoreSAnalysis.run`) with the baseline, transect spacing/extent, previously selected
   * images, land-water threshold and merge tolerance/direction — clearing the preview transects
   * first since the saga draws its own (colored by bias).
   * **Called by:
   * @see {ShoreSAnalysisSteps} - via the settings overlay's "Confirmar" button's onClick.
   * @memberof module:ShoreS
   * @returns {Promise<void>}
   */
  const handleRun = async () => {
    try {
      setSettingsTransectsEnabled(false);
      setIsSettings(false);
      setAnalysisDisabled(true);
      transectsObjects.forEach((object) => object.setMap(null));
      setTransectsObjects([]);
      await dispatch(
        ShoreSAnalysis.run(baseLineCoords, transectsStep, transectsExtent, {
          toleranceInDays,
          direction,
        }),
      );
    } catch (e) {
      setAnalysisDisabled(false);
      console.error('Analysis failed:', e);
    }
  };

  /**
   * @function handleRestart
   * @description Fully resets the analysis: removes the map layers drawn by the validation run
   * (transects, modeled/reference coastlines), clears the `shoreSAnalysis` result, tears down the
   * baseline/drawing manager, and resets every local step-config field back to its default.
   * **Called by:
   * @see {ShoreSAnalysisSteps} - via the "Reiniciar Análise" button's onClick.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const handleRestart = () => {
    dispatch(Map.removeShapeGroup(Metadata.FeatureType.TRANSECT));
    dispatch(Map.removeShapeGroup(REFERENCE_COASTLINE_GROUP));

    dispatch(ShoreSAnalysis.resetAnalysis());

    transectsObjects.forEach((o) => o.setMap(null));
    setTransectsObjects([]);

    if (baseLine) baseLine.overlay.setMap(null);
    setBaseLine(null);
    if (drawingManager.current) {
      drawingManager.current.cleanup();
      drawingManager.current.setMap(null);
      drawingManager.current = null;
    }

    setBaseLineCoords([]);
    setConfirmDrawing(false);
    setDrawingBaseline(false);
    setSettingsTransectsEnabled(false);
    setIsSettings(false);
    setAnalysisDisabled(false);
    setTransectsStep(100);
    setTransectsExtent(1600);
    setShowPlots(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: OVERLAY_PANEL_GAP }}>
      <ShapeEditControl editing={baselineEditing} visible={confirmDrawing} />
      {!analysisDisabled && !isSettings && !drawingBaseline && extraction && extraction.status !== 'IDLE' && (
        <Card style={{ padding: 12 }} elevation={3}>
          <Typography style={{ fontSize: 13, fontWeight: 600 }}>{t('forms.shoreS.extraction.title')}</Typography>

          {extraction.status === 'RUNNING' && (
            <>
              <Typography style={{ fontSize: 12, color: 'gray' }}>{t('forms.shoreS.extraction.running')}</Typography>
              <LinearProgress style={{ marginTop: 8 }} />
            </>
          )}

          {extraction.status === 'READY' && (
            <Typography style={{ fontSize: 12, color: 'gray' }}>
              {t('forms.shoreS.extraction.ready', {
                extracted: extraction.extracted,
                requested: extraction.requested,
              })}
            </Typography>
          )}

          {extraction.status === 'ERROR' && (
            <>
              <Typography color="error" style={{ fontSize: 12 }}>
                {extraction.error}
              </Typography>
              <Button
                color="primary"
                size="small"
                style={{ padding: 0, marginTop: 4 }}
                onClick={() => dispatch(ShoreSAnalysis.extractReferences(readSelectedImages(), extraction.key))}>
                {t('forms.shoreS.extraction.retry')}
              </Button>
            </>
          )}
        </Card>
      )}

      {!analysisDisabled && !isSettings && !drawingBaseline && (
        <Card style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }} elevation={3}>
          <div
            style={{
              fontWeight: 'bold',
              borderBottom: '1px solid gray',
              paddingBottom: 5,
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
            }}>
            {t('forms.shoreS.analysis.title')}
            <IconButton onClick={() => setShowHelpDialog(true)} color="primary" size="small" style={{ marginLeft: 8 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </div>

          <SimpleInfoDialog
            open={showHelpDialog}
            close={() => setShowHelpDialog(false)}
            title="forms.shoreS.analysis.help.title"
            texts={[
              'forms.shoreS.analysis.help.intro',
              'forms.shoreS.analysis.help.config',
              'forms.shoreS.analysis.help.purpose',
            ]}
          />

          <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Button
              style={{ justifyContent: 'left' }}
              color="primary"
              startIcon={<GestureOutlined />}
              onClick={() => {
                handleDrawBaseline();
                setDrawingBaseline(true);
              }}>
              {t('forms.csqueeze.result.drawBaseline')}
            </Button>

            <Tooltip
              title={!extractionReady && settingsTransectsEnabled ? t('forms.shoreS.extraction.waitingTooltip') : ''}>
              <span>
                <Button
                  style={{ justifyContent: 'left' }}
                  disabled={!settingsTransectsEnabled || !extractionReady}
                  color="primary"
                  startIcon={<LinearScale />}
                  onClick={() => {
                    setIsSettings(true);
                    setTransectsStep(100);
                    setTransectsExtent(1600);
                  }}>
                  {t('forms.csqueeze.result.configTransects')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Card>
      )}

      {!confirmDrawing && drawingBaseline && (
        <Card
          style={{ width: '100%', padding: 12, textAlign: 'center', marginTop: 10, fontSize: 13 }}
          elevation={3}>
          {t('forms.csqueeze.result.tipBaseline')}
          {'\n'}
          <Button color="secondary" component="label" htmlFor="file_import_baseline_kml_shore_s">
            {t('forms.shorelineAnalysis.transectsSettings.importBaseline')}
            <input
              style={{ display: 'none' }}
              id="file_import_baseline_kml_shore_s"
              accept=".kml"
              type="file"
              onClick={(e) => (e.target.value = null)}
              onChange={readBaselineFileData}
            />
          </Button>
        </Card>
      )}

      <Grow in={drawingBaseline} unmountOnExit>
        <Box position="absolute" marginTop={6} paddingTop={2} justifyContent="center" width="100%" zIndex={99}>
          <Dialog
            open={confirmDrawing}
            onClose={() => setConfirmDrawing(false)}
            aria-labelledby="confirm-dialog-title-shore-s"
            PaperProps={{ style: { padding: 20, borderRadius: 12, textAlign: 'center' } }}>
            <DialogTitle id="confirm-dialog-title-shore-s" style={{ textAlign: 'center' }}>
              {t('forms.csqueeze.result.confirmBaseline')}
            </DialogTitle>
            <DialogContent style={{ fontSize: '16px' }}>
              <p>{t('forms.csqueeze.result.confirmBaseline')}</p>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
              <Button onClick={handleRedo} color="primary">
                {t('forms.csqueeze.result.redo')}
              </Button>
              <Button
                onClick={() => {
                  setConfirmDrawing(true);
                  setSettingsTransectsEnabled(true);
                  dispatch(Map.completeDrawing());
                  setDrawingBaseline(false);
                }}
                color="primary"
                autoFocus>
                {t('forms.csqueeze.result.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Grow>

      <Grow in={isSettings} unmountOnExit paddingTop={0}>
        <Box
          id="settingsTransectsShoreS"
          display="contents"
          position="fixed !important"
          justifyContent="start"
          alignItems="center"
          width="100%"
          zIndex={99}
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <Card
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 5,
              padding: 12,
              textAlign: 'center',
            }}
            elevation={3}>
            <div style={{ height: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={t('forms.imageChooser.helpTooltip')}>
                <IconButton onClick={() => setShowImageChooserHelp(true)} size="small" style={{ padding: 4 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <SimpleInfoDialog
                open={showImageChooserHelp}
                close={() => setShowImageChooserHelp(false)}
                title={t('forms.transectEvolution.title')}
                text={t('forms.transectEvolution.TransectEvolutionInfoDialog')}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20 }}>
              <Typography gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setStepLabel')}
              </Typography>
              <Input
                type="number"
                value={transectsStep === 0 ? '' : transectsStep}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setTransectsStep(isNaN(value) ? 0 : value);
                }}
                placeholder="100"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20 }}>
              <Typography gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setExtentLabel')}
              </Typography>
              <Input
                type="number"
                value={transectsExtent === 0 ? '' : transectsExtent}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setTransectsExtent(isNaN(value) ? 0 : value);
                }}
                placeholder="1600"
              />
            </div>

            <p style={{ padding: 0, margin: 0 }}>ou:</p>
            <Button color="secondary" component="label" htmlFor="file_import_transects_kml_shore_s">
              {t('forms.shorelineAnalysis.transectsSettings.importTransectsStepsExtent')}
              <input
                style={{ display: 'none' }}
                id="file_import_transects_kml_shore_s"
                accept=".kml"
                type="file"
                onClick={(e) => (e.target.value = null)}
                onChange={readTransectsFileData}
              />
            </Button>

            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Tooltip title={t('forms.shoreS.merge.help.tooltip')}>
                <IconButton onClick={() => setShowMergeHelp(true)} size="small" style={{ padding: 4 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <SimpleInfoDialog
                open={showMergeHelp}
                close={() => setShowMergeHelp(false)}
                title="forms.shoreS.merge.help.title"
                texts={[
                  'forms.shoreS.merge.help.intro',
                  'forms.shoreS.merge.help.tolerance',
                  'forms.shoreS.merge.help.direction',
                  'forms.shoreS.merge.help.forward',
                  'forms.shoreS.merge.help.backward',
                  'forms.shoreS.merge.help.nearest',
                ]}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20 }}>
              <Typography gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shoreS.merge.toleranceLabel')}
              </Typography>
              <Input
                type="number"
                value={toleranceInDays}
                onChange={(e) => setToleranceInDays(Number(e.target.value))}
                inputProps={{ step: 1, min: 0 }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20 }}>
              <Typography gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shoreS.merge.directionLabel')}
              </Typography>
              <Select value={direction} onChange={(e) => setDirection(e.target.value)}>
                <MenuItem value="forward">{t('forms.shoreS.merge.directionForward')}</MenuItem>
                <MenuItem value="backward">{t('forms.shoreS.merge.directionBackward')}</MenuItem>
                <MenuItem value="nearest">{t('forms.shoreS.merge.directionNearest')}</MenuItem>
              </Select>
            </div>

            <Button
              disabled={!confirmTransectsConfigEnabled || loading}
              color="primary"
              onClick={handleRun}
              style={{ paddingBottom: 0, marginBottom: 0 }}>
              {t('forms.shorelineAnalysis.transectsSettings.confirm')}
            </Button>
            <Button
              color="secondary"
              style={{ padding: 0, margin: 0 }}
              onClick={() => {
                setIsSettings(false);
                transectsObjects.forEach((object) => object.setMap(null));
                setTransectsObjects([]);
              }}>
              {t('forms.csqueeze.result.cancel')}
            </Button>
          </Card>
        </Box>
      </Grow>

      {analysisDisabled && (
        <Card style={{ padding: 12 }} elevation={3}>
          {loading && <Typography variant="body2">{t('forms.shoreS.analysis.execute')}…</Typography>}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {errorMetrics && (
            <Box mb={1}>
              <Typography variant="subtitle2">{t('forms.shoreS.analysis.metricsTitle')}</Typography>
              <ShoreSMetricsPanel />

              <Button color="secondary" onClick={() => setShowPlots(true)} style={{ marginTop: 8 }}>
                {t('forms.shoreS.analysis.togglePlots')}
              </Button>
              <Button
                color="secondary"
                startIcon={<GetApp />}
                onClick={() => setExportOpen(true)}
                style={{ marginTop: 8 }}>
                {t('forms.shoreS.exports.title')}
              </Button>
            </Box>
          )}

          <Dialog open={showPlots} onClose={() => setShowPlots(false)} maxWidth="lg" fullWidth>
            <DialogTitle>{t('forms.shoreS.analysis.metricsTitle')}</DialogTitle>
            <DialogContent>{errorMetrics && <ShoreSErrorPlots />}</DialogContent>
            <DialogActions>
              <Button color="secondary" onClick={() => setShowPlots(false)}>
                {t('forms.shoreR.exports.close')}
              </Button>
            </DialogActions>
          </Dialog>
          <ShoreSExportDialog open={exportOpen} close={() => setExportOpen(false)} />

          <Button color="secondary" variant="outlined" style={{ width: '100%', marginTop: 8 }} onClick={handleRestart}>
            {t('forms.shorelineAnalysis.restartAnalysis').toUpperCase()}
          </Button>
        </Card>
      )}
    </div>
  );
};

export default ShoreSAnalysisSteps;
